import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import api from './api';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Redistribution from './pages/Redistribution';
import FFTask from './pages/FFTask';
import Monitoring from './pages/Monitoring';
import Profile from './pages/Profile';
import Import from './pages/Import';
import Home from './pages/Home';
import Reviews from './pages/Reviews';
import Advertising from './pages/Advertising';
import UnitEconomics from './pages/UnitEconomics';
import Hypothesis from './pages/Hypothesis';
import Audit from './pages/Audit';
import Strategy from './pages/Strategy';
import SalesFunnel from './pages/SalesFunnel';
import { PageStateProvider } from './hooks/usePageState';

function Layout({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_open');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const onToggle = (e) => setSidebarOpen(e.detail);
    window.addEventListener('sidebar-toggle', onToggle);
    return () => window.removeEventListener('sidebar-toggle', onToggle);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar user={user} onLogout={onLogout}/>
      <main style={{
        marginLeft: sidebarOpen ? 220 : 0,
        flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <Routes>
          <Route path="/"               element={<Home />} />
          <Route path="/monitoring"     element={<Monitoring />} />
          <Route path="/orders-regions" element={<Dashboard />} />
          <Route path="/redistribution" element={<Redistribution />} />
          <Route path="/ff-task"        element={<FFTask />} />
          <Route path="/advertising"    element={<Advertising />} />
          <Route path="/ai-analytics"   element={<Dashboard />} />
          <Route path="/hypotheses"     element={<Dashboard />} />
          <Route path="/reviews"         element={<Reviews />} />
          <Route path="/unit-economics"  element={<UnitEconomics />} />
          <Route path="/hypothesis" element={<Hypothesis />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/strategy" element={<Strategy />} />
          <Route path="/sales-funnel" element={<SalesFunnel />} />
          <Route path="/settings"       element={<Dashboard />} />
          <Route path="/import"         element={<Import />} />
          <Route path="/profile"        element={<Profile />} />
          <Route path="/info"           element={<Dashboard />} />
        </Routes>
      </main>
    </div>
  );
}

const CAT_GIFS = ["https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif","https://media.giphy.com/media/vFKqnCdLPNOKc/giphy.gif","https://media.giphy.com/media/mlvseq9yvZhba/giphy.gif","https://media.giphy.com/media/pFwRzOLfuGHok/giphy.gif","https://media.giphy.com/media/10dU7AN7xsi1I4/giphy.gif","https://media.giphy.com/media/nR4L10XlJcSeQ/giphy.gif","https://media.giphy.com/media/3oriO0OEd9QIDdllqo/giphy.gif","https://media.giphy.com/media/ule4vhcY1xEKQ/giphy.gif","https://media.giphy.com/media/GeimqsH0TLDt4tScGw/giphy.gif","https://media.giphy.com/media/BzyTuYCmvSORqs1ABM/giphy.gif","https://media.giphy.com/media/pY8jLmZw0ElqvVeRH4/giphy.gif","https://media.giphy.com/media/yWku98eNsMSZOEEWnC/giphy.gif","https://media.giphy.com/media/lh6EKzQdQghw784jvq/giphy.gif","https://media.giphy.com/media/BK1EfIsdkKZMY/giphy.gif","https://media.giphy.com/media/weIRemP4BI4U98HeGX/giphy.gif","https://media.giphy.com/media/12HZukMBlutpoQ/giphy.gif","https://media.giphy.com/media/EFnV02XHwNeIX65Gc7/giphy.gif","https://media.giphy.com/media/9n0CvokbUMaWhFuCkp/giphy.gif","https://media.giphy.com/media/X3ewes5lWn41o6hUjy/giphy.gif","https://media.giphy.com/media/TKa7fQzChHylCQ89to/giphy.gif","https://media.giphy.com/media/v6HmjWFF80ByC2JcRl/giphy.gif","https://media.giphy.com/media/In0Lpu4FVivjISX9HT/giphy.gif","https://media.giphy.com/media/2g6sCTsSoVuSfSxK4W/giphy.gif","https://media.giphy.com/media/cbm0J4DEIqKHsKxYHA/giphy.gif"];
function getRandomGif() {
  const key = 'cat_gif_weights_app';
  let weights = JSON.parse(localStorage.getItem(key) || 'null');
  if (!weights || weights.length !== CAT_GIFS.length) weights = CAT_GIFS.map(() => 10);
  const total = weights.reduce((s,w) => s+w, 0);
  let rand = Math.random() * total;
  let chosen = 0;
  for (let i = 0; i < weights.length; i++) { rand -= weights[i]; if (rand <= 0) { chosen = i; break; } }
  weights = weights.map((w, i) => i === chosen ? Math.max(1, w-4) : Math.min(10, w+1));
  localStorage.setItem(key, JSON.stringify(weights));
  return CAT_GIFS[chosen];
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const catGif = useRef(getRandomGif());

  // Восстанавливаем блюр при любом обновлении страницы
  useEffect(() => {
    if (localStorage.getItem('bystats_blur_ids') === '1') {
      document.body.classList.add('blur-wb-ids');
      document.documentElement.classList.add('blur-wb-ids');
    }
  }, []);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(r => setUser(r.data))
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);
  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };
  if (loading) return <div style={{ minHeight:'100vh', background:'#0a0f1a' }}/>;
  return (
    <PageStateProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login onLogin={setUser} />} />
          <Route path="/*" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
    </PageStateProvider>
  );
}
